import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Calendar, Clock, MapPin, Users, Bell, ChevronLeft, ChevronRight, Send, Mail, CalendarCheck2, RefreshCw, CalendarPlus } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageTitle } from "./Layout";
import { Field } from "../../components/admin/Fields";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { DateTimePicker, formatDMY } from "../../components/ui/datetime-picker";
import AppointmentRequestModal from "../../components/admin/AppointmentRequestModal";

const CATEGORIES = [
  ["genel", "Genel", "#64748b"],
  ["toplanti", "Toplantı", "#2563eb"],
  ["etkinlik", "Etkinlik", "#9333ea"],
  ["yayin", "Yayın / TV", "#dc2626"],
  ["seyahat", "Seyahat", "#059669"],
  ["kisisel", "Kişisel", "#f59e0b"],
  ["hatirlatma", "Hatırlatma", "#334155"],
];
const catColor = (k) => (CATEGORIES.find((c) => c[0] === k) || CATEGORIES[0])[2];
const catLabel = (k) => (CATEGORIES.find((c) => c[0] === k) || CATEGORIES[0])[1];

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const z = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};
const fromLocalInput = (s) => (s ? new Date(s).toISOString() : "");
const fmtDateTime = (iso) => {
  if (!iso) return "";
  return formatDMY(iso, true);
};
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const z = (n) => String(n).padStart(2, "0");
  return `${z(d.getHours())}:${z(d.getMinutes())}`;
};

const blank = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return {
    title: "",
    start_at: d.toISOString(),
    end_at: "",
    location: "",
    with_whom: "",
    description: "",
    category: "genel",
    remind_minutes: [60],
    channels: ["telegram"],
    color: "",
  };
};

function RemindersInput({ value = [], onChange }) {
  const presets = [
    [60,   "1 saat önce"],
    [120,  "2 saat önce"],
    [180,  "3 saat önce"],
    [240,  "4 saat önce"],
    [360,  "6 saat önce"],
    [480,  "8 saat önce"],
    [1440, "1 gün önce"],
  ];
  const toggle = (m) => onChange(value.includes(m) ? value.filter((x) => x !== m) : [...value, m].sort((a, b) => a - b));
  return (
    <div className="flex flex-wrap gap-2" data-testid="event-reminders">
      {presets.map(([m, l]) => (
        <button
          key={m}
          type="button"
          onClick={() => toggle(m)}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors inline-flex items-center gap-1 ${
            value.includes(m) ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
          }`}
          data-testid={`reminder-${m}`}
        >
          {value.includes(m) ? "✓" : "+"} {l}
        </button>
      ))}
    </div>
  );
}

function ChannelsInput({ value = [], onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const Row = ({ k, label, hint }) => {
    const checked = value.includes(k);
    return (
      <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-amber-400 bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`} data-testid={`channel-row-${k}`}>
        <input type="checkbox" checked={checked} onChange={() => toggle(k)} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid={`channel-${k}`} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-stone-800">{label}</div>
          <div className="text-[11px] text-stone-500 leading-snug">{hint}</div>
        </div>
      </label>
    );
  };
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="event-channels">
      <Row k="telegram" label="Telegram'dan gönder" hint="Bota bağlıysa saatinde özel sohbete mesaj düşer" />
      <Row k="email" label="E-posta gönder" hint="Bildirim e-postan aktifse hatırlatma oraya ulaşır" />
    </div>
  );
}

function ShareDialog({ eventId, onClose }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!/^.+@.+\..+$/.test(to.trim())) return toast.error("Geçerli bir e-posta adresi girin");
    setBusy(true);
    try {
      await api.post(`/admin/calendar/${eventId}/share`, { to: to.trim(), message });
      toast.success(`Davetiye ${to} adresine gönderildi`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="share-dialog">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2"><Mail size={18} className="text-amber-600" />Davetiyeyi paylaş</h3>
            <p className="text-xs text-stone-500">Alıcıya olgu.eu'dan şık bir davet gider; e-postanın içinde Google / Outlook / .ics ekle düğmeleri çıkar.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={16} /></button>
        </div>
        <Field label="Alıcı e-posta">
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="ornek@domain.com" autoFocus data-testid="share-to" />
        </Field>
        <Field label="Kısa mesaj (opsiyonel)">
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Davetiye içinde yan sütunda altın rengi şeritte görünür." data-testid="share-message" />
        </Field>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>Vazgeç</Button>
          <Button type="button" onClick={send} disabled={busy} data-testid="share-send"><Send size={14} className="mr-1" />{busy ? "Gönderiliyor…" : "Gönder"}</Button>
        </div>
      </div>
    </div>
  );
}

function EventForm({ initial, onSave, onCancel, onDelete }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [errors, setErrors] = useState({}); // field -> error message
  const set = (k) => (v) => {
    setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };
  // Validate the whole form, return the field->error map
  const validate = (vals) => {
    const errs = {};
    if (!vals.title || !vals.title.trim()) errs.title = "Başlık gerekli";
    if (!vals.start_at) errs.start_at = "Başlangıç tarihi/saati gerekli";
    if (vals.end_at && vals.start_at && new Date(vals.end_at) < new Date(vals.start_at)) {
      errs.end_at = "Bitiş, başlangıçtan sonra olmalı";
    }
    return errs;
  };
  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Scroll first invalid field into view
      const first = document.querySelector(`[data-testid="event-form"] [data-error="true"]`);
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error(`Eksik alan var: ${Object.values(errs).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      await onSave(f);
      toast.success(initial.id ? "Etkinlik güncellendi" : "Etkinlik eklendi");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="event-form" noValidate>
      <Field label="Başlık *">
        <Input
          value={f.title}
          onChange={set("title")}
          placeholder="Örn: Yeni Düzen röportajı"
          className={errors.title ? "border-red-400 focus:border-red-500 ring-red-100" : ""}
          data-error={errors.title ? "true" : undefined}
          data-testid="event-title"
        />
        {errors.title && (
          <p className="text-xs text-red-600 mt-1" data-testid="event-title-error">
            {errors.title}
          </p>
        )}
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Başlangıç *">
          <div data-error={errors.start_at ? "true" : undefined}>
            <DateTimePicker
              value={f.start_at}
              onChange={(iso) => { setF((p) => ({ ...p, start_at: iso })); if (errors.start_at) setErrors((e) => ({ ...e, start_at: null })); }}
              testId="event-start"
              error={!!errors.start_at}
            />
          </div>
          {errors.start_at && (
            <p className="text-xs text-red-600 mt-1" data-testid="event-start-error">
              {errors.start_at}
            </p>
          )}
        </Field>
        <Field label="Bitiş (opsiyonel)">
          <div data-error={errors.end_at ? "true" : undefined}>
            <DateTimePicker
              value={f.end_at}
              onChange={(iso) => { setF((p) => ({ ...p, end_at: iso })); if (errors.end_at) setErrors((e) => ({ ...e, end_at: null })); }}
              testId="event-end"
              error={!!errors.end_at}
            />
          </div>
          {errors.end_at && (
            <p className="text-xs text-red-600 mt-1" data-testid="event-end-error">
              {errors.end_at}
            </p>
          )}
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Kiminle">
          <Input value={f.with_whom} onChange={set("with_whom")} placeholder="Ad soyad / ekip" data-testid="event-with-whom" />
        </Field>
        <Field label="Nerede">
          <Input value={f.location} onChange={set("location")} placeholder="Adres veya bağlantı" data-testid="event-location" />
        </Field>
      </div>
      <Field label="Kategori">
        <div className="flex flex-wrap gap-2" data-testid="event-category">
          {CATEGORIES.map(([k, l, c]) => (
            <button
              key={k}
              type="button"
              onClick={() => setF({ ...f, category: k })}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors inline-flex items-center gap-1.5 ${
                f.category === k ? "text-white" : "text-stone-600 bg-white"
              }`}
              style={f.category === k ? { background: c, borderColor: c } : { borderColor: "#d6d3d1" }}
              data-testid={`event-cat-${k}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: c }} />
              {l}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Hatırlatmalar" hint="Birden fazla seçebilirsin. Etkinlikten şu süre kadar önce bildirim atılır.">
        <RemindersInput value={f.remind_minutes || []} onChange={set("remind_minutes")} />
      </Field>
      <Field label="Hatırlatma kanalları" hint="Nereden bildirim gelsin? İkisini birden işaretleyebilirsin.">
        <ChannelsInput value={f.channels || ["telegram"]} onChange={set("channels")} />
      </Field>
      <Field label="Not">
        <Textarea rows={3} value={f.description} onChange={set("description")} placeholder="Hazırlık, link, konu notları…" data-testid="event-description" />
      </Field>
      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={busy} data-testid="event-save">
          {busy ? "Kaydediliyor…" : initial.id ? "Güncelle" : "Kaydet"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="event-cancel">
          Vazgeç
        </Button>
        {initial.id && (
          <>
            <Button type="button" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => setShareOpen(true)} data-testid="event-share">
              <Mail size={15} className="mr-1" /> Davetiye gönder
            </Button>
            <Button type="button" variant="ghost" className="ml-auto text-red-600 hover:text-red-700" onClick={() => onDelete(initial.id)} data-testid="event-delete">
              <Trash2 size={15} className="mr-1" />
              Sil
            </Button>
          </>
        )}
      </div>
      {shareOpen && <ShareDialog eventId={initial.id} onClose={() => setShareOpen(false)} />}
    </form>
  );
}

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const TR_DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function Month({ monthStart, events, onPickDay, onPickEvent, onReschedule }) {
  const first = new Date(monthStart);
  first.setDate(1);
  first.setHours(0, 0, 0, 0);
  // Monday-first grid
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(1 - offset);
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  const byDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = new Date(ev.start_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push(ev);
    }
    return map;
  }, [events]);
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const [dragOver, setDragOver] = useState("");

  const handleDrop = (e, targetDay) => {
    e.preventDefault();
    setDragOver("");
    const eid = e.dataTransfer.getData("text/event-id");
    if (!eid || !onReschedule) return;
    onReschedule(eid, targetDay);
  };

  return (
    <div data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 text-[11px] uppercase tracking-widest text-stone-400 mb-2">
        {TR_DAYS.map((d) => (
          <div key={d} className="px-2 py-1 font-semibold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {days.map((d, i) => {
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const inMonth = d.getMonth() === first.getMonth();
          const list = byDay[key] || [];
          const isToday = key === todayKey;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isOver = dragOver === key;
          return (
            <div
              key={i}
              onClick={() => onPickDay(d)}
              onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver((v) => (v === key ? "" : v))}
              onDrop={(e) => handleDrop(e, d)}
              className={`min-h-[96px] sm:min-h-[120px] text-left rounded-xl border p-1.5 transition-all flex flex-col cursor-pointer ${
                inMonth ? (isWeekend ? "bg-amber-50/40" : "bg-white") : "bg-stone-50 opacity-60"
              } ${isToday ? "ring-2 ring-amber-500 border-amber-500" : "border-stone-200 hover:border-stone-400 hover:shadow-sm"} ${
                isOver ? "ring-2 ring-emerald-500 border-emerald-500 bg-emerald-50/60" : ""
              }`}
              data-testid={`calendar-day-${key}`}
            >
              <span className={`text-xs px-1 ${isToday ? "font-bold text-amber-700" : inMonth ? "text-stone-600" : "text-stone-400"}`}>
                {d.getDate()}
                {isToday && <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-700">bugün</span>}
              </span>
              <div className="mt-1 space-y-1 flex-1 overflow-hidden">
                {list.slice(0, 3).map((ev) => {
                  const color = ev.color || catColor(ev.category);
                  return (
                    <div
                      key={ev.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData("text/event-id", ev.id); e.stopPropagation(); }}
                      onClick={(e) => { e.stopPropagation(); onPickEvent(ev); }}
                      className="group block truncate text-[11px] rounded-md px-1.5 py-1 text-white cursor-grab active:cursor-grabbing shadow-sm hover:shadow transition-shadow"
                      style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`, borderLeft: `3px solid ${color}` }}
                      data-testid={`calendar-event-${ev.id}`}
                      title={`${ev.title} — sürükleyip başka güne bırakabilirsin`}
                    >
                      <span className="font-semibold">{fmtTime(ev.start_at)}</span> <span className="opacity-95">{ev.title}</span>
                    </div>
                  );
                })}
                {list.length > 3 && <span className="text-[10px] text-stone-400 px-1">+{list.length - 3} daha</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UpcomingStrip({ events, onPickEvent }) {
  if (!events.length) {
    return (
      <section className="bg-white border rounded-xl p-4 flex items-center gap-3" data-testid="calendar-upcoming">
        <Bell size={16} className="text-stone-400" />
        <p className="text-sm text-stone-400">Yaklaşan etkinlik yok. Takvimden bir güne tıklayarak ekleyebilirsiniz.</p>
      </section>
    );
  }
  return (
    <section className="bg-white border rounded-xl p-4" data-testid="calendar-upcoming">
      <div className="flex items-center gap-2 mb-3">
        <Bell size={14} className="text-stone-700" />
        <h3 className="font-semibold text-sm">Yaklaşan</h3>
        <span className="text-[11px] uppercase tracking-widest text-stone-400 ml-auto">{events.length}</span>
      </div>
      <ul className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {events.map((ev) => (
          <li
            key={ev.id}
            onClick={() => onPickEvent(ev)}
            className="min-w-[220px] max-w-[260px] shrink-0 snap-start flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-stone-400 bg-white"
            data-testid={`upcoming-${ev.id}`}
          >
            <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: ev.color || catColor(ev.category) }} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{ev.title}</p>
              <p className="text-[11px] text-stone-500 flex items-center gap-1 mt-1"><Clock size={10} />{fmtDateTime(ev.start_at)}</p>
              {(ev.location || ev.with_whom) && (
                <p className="text-[11px] text-stone-400 flex items-center gap-2 mt-0.5 truncate">
                  {ev.location && (<span className="inline-flex items-center gap-0.5"><MapPin size={10} />{ev.location}</span>)}
                  {ev.with_whom && (<span className="inline-flex items-center gap-0.5"><Users size={10} />{ev.with_whom}</span>)}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function CalendarAdmin() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [editing, setEditing] = useState(null); // {} or event
  const [requestingAppt, setRequestingAppt] = useState(false);
  const monthFrom = new Date(month);
  monthFrom.setDate(monthFrom.getDate() - 7);
  const monthTo = new Date(month);
  monthTo.setMonth(monthTo.getMonth() + 1);
  monthTo.setDate(monthTo.getDate() + 7);
  const { data: events = [] } = useQuery({
    queryKey: ["admin", "events", monthFrom.toISOString(), monthTo.toISOString()],
    queryFn: () =>
      api.get(`/admin/calendar?from_=${encodeURIComponent(monthFrom.toISOString())}&to=${encodeURIComponent(monthTo.toISOString())}`).then((r) => r.data),
  });
  const { data: upcoming = [] } = useQuery({
    queryKey: ["admin", "events-upcoming"],
    queryFn: () => api.get("/admin/calendar/upcoming").then((r) => r.data),
  });
  const { data: gcal } = useQuery({
    queryKey: ["admin", "gcal", "status"],
    queryFn: () => api.get("/admin/gcal/status").then((r) => r.data).catch(() => null),
    refetchInterval: 60000,
  });
  const [syncing, setSyncing] = useState(false);
  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/gcal/sync");
      toast.success(`Google Takvim senkron tamam — +${data.imported || 0} / ~${data.updated || 0} / −${data.deleted || 0}`);
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
      qc.invalidateQueries({ queryKey: ["admin", "events-upcoming"] });
      qc.invalidateQueries({ queryKey: ["admin", "gcal", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSyncing(false);
  };
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "events"] });
    qc.invalidateQueries({ queryKey: ["admin", "events-upcoming"] });
  };
  const save = async (f) => {
    if (f.id) await api.put(`/admin/calendar/${f.id}`, f);
    else await api.post("/admin/calendar", f);
    invalidate();
    setEditing(null);
  };
  const remove = async (id) => {
    if (!window.confirm("Etkinliği silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/calendar/${id}`);
    toast.success("Etkinlik silindi");
    invalidate();
    setEditing(null);
  };
  const reschedule = async (eid, targetDay) => {
    const ev = events.find((e) => e.id === eid);
    if (!ev) return;
    const src = new Date(ev.start_at);
    const dst = new Date(targetDay);
    dst.setHours(src.getHours(), src.getMinutes(), 0, 0);
    if (src.toDateString() === dst.toDateString()) return;
    try {
      const payload = { ...ev, start_at: dst.toISOString() };
      if (ev.end_at) {
        const diffMs = new Date(ev.end_at) - src;
        payload.end_at = new Date(dst.getTime() + diffMs).toISOString();
      }
      await api.put(`/admin/calendar/${eid}`, payload);
      toast.success(`"${ev.title}" ${dst.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })} tarihine taşındı`);
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const monthLabel = `${TR_MONTHS[month.getMonth()]} ${month.getFullYear()}`;
  const shift = (n) => {
    const d = new Date(month);
    d.setMonth(d.getMonth() + n);
    setMonth(d);
  };
  const goToday = () => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    setMonth(d);
  };

  return (
    <>
      <PageTitle
        title="Takvim"
        sub="Sadece size özel: yaklaşan buluşmalar, programlar, yayınlar. Hatırlatmalar Telegram üzerinden gelir."
        action={
          <div className="flex items-center gap-2">
            {gcal?.connected ? (
              <button
                type="button"
                onClick={syncNow}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-60"
                data-testid="calendar-gcal-sync"
                title={`Google Takvim bağlı · ${gcal.connected_email || ""}`}
              >
                <CalendarCheck2 size={12} />
                Google <RefreshCw size={11} className={syncing ? "animate-spin" : ""} />
              </button>
            ) : (
              <a
                href="/admin/ayarlar?tab=integrations"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border border-stone-300 text-stone-500 hover:border-amber-400 hover:text-amber-700 transition-colors"
                data-testid="calendar-gcal-connect"
              >
                <CalendarCheck2 size={12} /> Google bağla
              </a>
            )}
            <Button variant="outline" onClick={() => setRequestingAppt(true)} data-testid="calendar-request-appt">
              <CalendarPlus size={15} className="mr-1" />
              Randevu Talep Et
            </Button>
            <Button onClick={() => setEditing(blank())} data-testid="calendar-new">
              <Plus size={15} className="mr-1" />
              Yeni etkinlik
            </Button>
          </div>
        }
      />
      <UpcomingStrip events={upcoming} onPickEvent={setEditing} />
      <div className="bg-white border rounded-xl p-4 sm:p-5 mt-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev">
              <ChevronLeft size={16} />
            </Button>
            <h2 className="font-serif text-xl font-bold min-w-[180px] text-center">{monthLabel}</h2>
            <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="calendar-next">
              <ChevronRight size={16} />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today">
            <Calendar size={13} className="mr-1" />
            Bugün
          </Button>
        </div>
        <Month
          monthStart={month}
          events={events}
          onPickDay={(d) => {
            const start = new Date(d);
            start.setHours(9, 0, 0, 0);
            setEditing({ ...blank(), start_at: start.toISOString() });
          }}
          onPickEvent={(ev) => setEditing(ev)}
          onReschedule={reschedule}
        />
      </div>
      <p className="mt-4 text-xs text-stone-400 flex items-center gap-1.5" data-testid="calendar-tg-tip">
        <Send size={12} /> Telegram'da <code className="bg-stone-100 border rounded px-1">/takvim</code> yazarak etkinlik ekleyebilirsiniz. Kurulum için{" "}
        <a href="/admin/ayarlar" className="underline text-stone-700">Ayarlar → Entegrasyonlar</a>.
      </p>
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => {
            // Prevent accidental close when user has unsaved data in a new event
            const hasData = (editing.title || "").trim() || editing.location || editing.description || editing.with_whom;
            if (!editing.id && hasData) {
              if (!window.confirm("Girilen bilgiler kaybolacak. Yine de kapatılsın mı?")) return;
            }
            setEditing(null);
          }}
          data-testid="event-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
              <h2 className="font-serif text-xl font-bold">{editing.id ? "Etkinliği düzenle" : "Yeni etkinlik"}</h2>
              <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-900" data-testid="event-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <EventForm initial={editing} onSave={save} onCancel={() => setEditing(null)} onDelete={remove} />
            </div>
          </div>
        </div>
      )}
      <AppointmentRequestModal open={requestingAppt} onClose={() => setRequestingAppt(false)} />
    </>
  );
}
